import { Button, Checkbox, clx, Input, Label, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { money } from "../lib/format"
import { t, ts } from "../lib/i18n"
import { errorMessage, hqApi, type OrderDetail } from "../lib/medusa"
import { parseAmount } from "../lib/payment-links"
import { newRequestId } from "../lib/stock-changes"

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * One order's delivery charge changed or taken off (owners, 14 Oct 2026: a 1,500 € order showed
 * 1,505 € because of home delivery's 5 €). Medusa's own order edit: the total follows, a cash order
 * collects the new total at hand-over, and what a paid order paid above it can be refunded here.
 */
export function DeliveryCharge({ order, current, onClose }: { order: OrderDetail; current: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const [requestId] = useState(newRequestId)
  const [mode, setMode] = useState<"free" | "other">("free")
  const [other, setOther] = useState("")
  const [note, setNote] = useState("")
  const [refund, setRefund] = useState(true)
  const [notify, setNotify] = useState(true)
  const card = order.payment.kind === "card"
  const paid = order.payment.refundable > 0

  const typed = parseAmount(other)
  const amount = mode === "free" ? 0 : typed
  const valid = amount != null && Number.isFinite(amount) && amount >= 0 && amount <= 500 && round2(amount) !== round2(current)
  const delta = valid ? round2(amount - current) : 0
  const newTotal = round2(order.total + delta)
  const back = paid && delta < 0 ? Math.min(order.payment.refundable, -delta) : 0
  const owes = paid && delta > 0 ? delta : 0

  const save = useMutation({
    mutationFn: () => hqApi.deliveryCharge(order.id, { amount: round2(amount ?? 0), request_id: requestId, note: note.trim() || null, refund: back > 0 && refund, notify }),
    onSuccess: (body) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      if (body.replay) toast.success(t("Already done."))
      else if (body.refund_error) toast.warning(t("Saved, but the refund failed: {error} Refund it with Refund at the top.", { error: ts(body.refund_error) }))
      else
        toast.success(
          [
            amount === 0 ? t("Delivery charge taken off.") : t("Delivery charge changed."),
            body.refunded > 0 ? (card ? t("{amount} refunded to the card.", { amount: money(body.refunded) }) : t("{amount} refunded (recorded).", { amount: money(body.refunded) })) : null,
            body.owed > 0 ? t("The customer owes {amount} more.", { amount: money(body.owed) }) : null,
          ]
            .filter(Boolean)
            .join(" ")
        )
      onClose()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  async function confirm() {
    if (!valid) return
    const moneyLine =
      back > 0 && refund
        ? card
          ? t("{amount} goes back to the customer's card through Stripe. This is real money and cannot be undone.", { amount: money(back) })
          : t("The refund is recorded. Give the {amount} back in cash yourself.", { amount: money(back) })
        : owes > 0
          ? t("The customer owes about {amount} more: settle it in a DM (cash at hand-over, or a payment link).", { amount: money(owes) })
          : null
    const ok = await prompt({
      title: amount === 0 ? t("Take the delivery charge off {ref}?", { ref: order.ref }) : t("Change {ref}'s delivery charge to {amount}?", { ref: order.ref, amount: money(amount ?? 0) }),
      description: [t("The total becomes {amount}.", { amount: money(newTotal) }), moneyLine].filter(Boolean).join(" "),
      confirmText: amount === 0 ? t("Take it off") : t("Change the charge"),
      cancelText: t("Back"),
      variant: "confirmation",
    })
    if (ok) save.mutate()
  }

  const pill = (active: boolean) =>
    clx(
      "txt-compact-xsmall-plus h-8 rounded-full border px-3 transition-colors",
      active ? "bg-ui-fg-base text-ui-bg-base border-transparent" : "bg-ui-bg-base text-ui-fg-subtle border-ui-border-base"
    )

  return (
    <div className="bg-ui-bg-subtle flex w-full flex-col gap-y-2.5 rounded-lg p-3">
      <div role="group" aria-label={t("Delivery charge")} className="flex flex-wrap gap-1.5">
        <button type="button" aria-pressed={mode === "free"} className={pill(mode === "free")} onClick={() => setMode("free")} disabled={current === 0}>{t("Free delivery (€0)")}</button>
        <button type="button" aria-pressed={mode === "other"} className={pill(mode === "other")} onClick={() => setMode("other")}>{t("Another amount")}</button>
      </div>
      {mode === "other" && (
        <div className="flex items-center gap-x-2">
          <Label htmlFor={`delivery-${order.id}`} className="txt-compact-small">{t("Delivery charge")}</Label>
          <div className="w-24">
            <Input id={`delivery-${order.id}`} size="small" inputMode="decimal" value={other} onChange={(event) => setOther(event.target.value)} aria-invalid={other !== "" && !valid} />
          </div>
          <Text size="small" className="text-ui-fg-subtle">€</Text>
        </div>
      )}
      <div className="flex flex-col gap-y-1">
        <Label htmlFor={`delivery-note-${order.id}`} className="txt-compact-small">{t("Why (only you see it)")}</Label>
        <Input id={`delivery-note-${order.id}`} size="small" value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} placeholder={t("e.g. agreed price")} />
      </div>
      {valid && <Text size="xsmall" className="text-ui-fg-subtle">{t("New total {amount}.", { amount: money(newTotal) })}</Text>}
      {back > 0 && (
        <div className="flex items-start gap-x-2">
          <Checkbox id={`delivery-refund-${order.id}`} checked={refund} onCheckedChange={(value) => setRefund(value === true)} className="mt-0.5" />
          <Label htmlFor={`delivery-refund-${order.id}`} className="txt-compact-small">
            {card ? t("Refund the difference to the card ({amount})", { amount: money(back) }) : t("Record the difference as refunded ({amount}), and give it back in cash", { amount: money(back) })}
          </Label>
        </div>
      )}
      {back > 0 && refund && (
        <div className="flex items-start gap-x-2">
          <Checkbox id={`delivery-notify-${order.id}`} checked={notify} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
          <Label htmlFor={`delivery-notify-${order.id}`} className="txt-compact-small">{t("Email the customer about the refund")}</Label>
        </div>
      )}
      {owes > 0 && <Text size="xsmall" className="text-ui-fg-subtle">{t("The customer owes about {amount} more: settle it in a DM (cash at hand-over, or a payment link).", { amount: money(owes) })}</Text>}
      {!paid && valid && <Text size="xsmall" className="text-ui-fg-muted">{t("Nothing has been paid yet: the customer pays the new total at hand-over.")}</Text>}
      <div className="flex flex-wrap gap-2">
        <Button size="small" onClick={() => void confirm()} isLoading={save.isPending} disabled={!valid}>{amount === 0 ? t("Take it off") : t("Change the charge")}</Button>
        <Button size="small" variant="secondary" onClick={onClose} disabled={save.isPending}>{t("Cancel")}</Button>
      </div>
    </div>
  )
}
